import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) { symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); } keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

import React, { forwardRef, useRef, useImperativeHandle, useState, useEffect } from 'react';
import { CancelIcon, MenuIcon, colors } from '@chegg-ui/theme';
import { smoothScrollElement, useLayoutEffectIsomorphic } from '@chegg-ui/utils';
import { clickBaseEvent, viewBaseEvent } from '@chegg-me-components/utils';
import { rioClient } from '../../../shared/rio';
import { MobileNavOverlay, MobileNavContent, MobileNavCloseButton, MobileNavScrollContainer, MobileAccordionDivider, SecondLevelNavLink, LinkAccordionHeader, UserInfoUL, UserInfoLI, UserProfileLink, SmallPaddedAvatar, UserTextContainer, UserProfileText, UserEmailText, UserInfoLink, Copyright, MenuFooterTerms, MenuFooter, mobileLinkHeightInPixels, VisuallyHidden } from '../../../styles';
import { NavMobileTopLevelLink, NavMobileTopLevelMenuItem, StyledAccordion, NavLinkText, MenuButton, LinkAccordionPanel } from '../../../styles.retrofit';
import useNavMenuBehavior from '../../useNavMenuBehavior';
import UserInfoLoader from '../desktop/UserInfoLoader';
var MobileHamburgerMenu = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var activeSubLink = _ref.activeSubLink,
      envUrl = _ref.envUrl,
      user = _ref.user,
      navLinks = _ref.navLinks,
      userLinks = _ref.userLinks,
      userDataLoading = _ref.userDataLoading;

  var _useNavMenuBehavior = useNavMenuBehavior(),
      navMenuRef = _useNavMenuBehavior.navMenuRef,
      navHamburgerButtonRef = _useNavMenuBehavior.navHamburgerButtonRef,
      navMenuOpen = _useNavMenuBehavior.navMenuOpen,
      setNavMenuOpen = _useNavMenuBehavior.setNavMenuOpen,
      onNavKeyDown = _useNavMenuBehavior.onNavKeyDown,
      onNavCloseButtonClick = _useNavMenuBehavior.onNavCloseButtonClick;
  /* istanbul ignore next */


  var navItems = _toConsumableArray(navLinks.items);

  if (navLinks.subItems) {
    navItems = [].concat(_toConsumableArray(navItems), _toConsumableArray(navLinks.subItems));
  }

  useImperativeHandle(ref, function () {
    return navMenuRef.current;
  });
  var topLevelLinksRef = useRef(navItems.map(function () {
    return /*#__PURE__*/React.createRef();
  }));
  var scrollContainerRef = useRef(null);
  var userUlRef = useRef(null);

  var _useState = useState(-1),
      _useState2 = _slicedToArray(_useState, 2),
      activeAccordionIndex = _useState2[0],
      setActiveAccordionIndex = _useState2[1];

  useEffect(function () {
    if (navMenuOpen) {
      var activeLinkIndexForType = navItems.findIndex(function (linkObj) {
        return linkObj.text === activeSubLink;
      });
      setActiveAccordionIndex(activeLinkIndexForType);
    }
  }, [navMenuOpen]); // we're just scrolling the active link to the top of the scroll container after opening, thus safe for SSR

  useLayoutEffectIsomorphic(function () {
    if (navMenuOpen && activeAccordionIndex >= 0) {
      var _topLevelLinksRef$cur;

      /* istanbul ignore next */
      var focusTarget =
      /* istanbul ignore next */
      (_topLevelLinksRef$cur = topLevelLinksRef.current[activeAccordionIndex]) === null || _topLevelLinksRef$cur === void 0 ? void 0 : _topLevelLinksRef$cur.current;
      /* istanbul ignore else */

      if (focusTarget) focusTarget.focus();
      var scrollTop = activeAccordionIndex * mobileLinkHeightInPixels;
      /* istanbul ignore else */

      if (scrollContainerRef.current) {
        smoothScrollElement(scrollContainerRef.current, {
          top: scrollTop
        });
      }

      document.body.style.overflow = 'hidden';
    }

    return function () {
      document.body.style.overflow = 'auto';
    };
  }, [navMenuOpen, activeAccordionIndex]);
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(MenuButton, {
    onClick: function onClick() {
      setNavMenuOpen(true);
      rioClient.sendEvent(_objectSpread(_objectSpread({}, clickBaseEvent), {}, {
        data: {
          interaction: {
            type: 'expand',
            element: {
              type: 'icon',
              name: 'nav hamburger',
              region_name: 'side nav'
            }
          }
        }
      }));
    },
    screenReaderOnlyText: "navigation",
    "aria-expanded": navMenuOpen,
    ref: navHamburgerButtonRef,
    "data-test": "header-mobile-hamburger-button"
  }, /*#__PURE__*/React.createElement(MenuIcon, null)), /*#__PURE__*/React.createElement(MobileNavOverlay, {
    isOpen: navMenuOpen
  }), /*#__PURE__*/React.createElement(MobileNavContent, {
    ref: navMenuRef,
    isOpen: navMenuOpen,
    onKeyDown: onNavKeyDown,
    role: "navigation",
    "aria-label": "main",
    "data-test": "header-mobile-navigation"
  }, /*#__PURE__*/React.createElement(MobileNavScrollContainer, {
    ref: scrollContainerRef,
    isOpen: navMenuOpen
  }, /*#__PURE__*/React.createElement(MobileNavCloseButton, {
    screenReaderOnlyText: "close navigation",
    onClick: onNavCloseButtonClick,
    "data-test": "header-mobile-close-hamburger-button"
  }, /*#__PURE__*/React.createElement(CancelIcon, {
    color: colors.dark
  })), function () {
    var items = []; // An array to maintain the nav-items in a way they would be rendered

    var counter = 0; // while() loop to iterate over navItems array using `counter` to keep the current index

    while (counter < navItems.length) {
      var accordionTabs = []; // This while loop would collect all the consecutive nav-items with a menu and push it into the `accordionTabs` array

      while (Object.keys(navItems[counter]).includes('menuItems')) {
        accordionTabs.push(_objectSpread(_objectSpread({}, navItems[counter]), {}, {
          position: counter
        }));
        counter++; // after every push to `accordionTabs`, increasing `counter` count
      }

      if (accordionTabs.length > 0) {
        /* If `accordionTabs` array have length greater than 0, then it means that there were few items which had a menu, so pushing
        `accordionTabs` as one single item in `items` array */
        items.push(accordionTabs);
      } else {
        /* If `accordionTabs` have length 0, then it means that there were no items with a menu, hence pushing the current item
        as it is in the `items` array */
        items.push(_objectSpread(_objectSpread({}, navItems[counter]), {}, {
          position: counter
        }));
        counter++; // after pushing a non-menu item to `items` array, increasing the `counter` count
      }
    }
    /* Iterating over `items` array and rendering based to the type of item found. If the item is an array, then it is a set
    of nav-items with menu. Otherwise it is just a plain link item with no menu */


    return items.map(function (item) {
      if (Array.isArray(item)) {
        return /*#__PURE__*/React.createElement(StyledAccordion, {
          id: "mobile-hamburger-accordion",
          activeTabIndex: activeAccordionIndex - 1,
          onChangeIndex: function onChangeIndex(i) {
            setActiveAccordionIndex(i < 0 ? i : i + 1);
          },
          tabs: item.map(function (navMenuItem) {
            var _ref2 = navMenuItem,
                text = _ref2.text,
                menuItems = _ref2.menuItems,
                ItemIcon = _ref2.icon,
                _onClick = _ref2.onClick,
                position = _ref2.position;
            var isActive = activeAccordionIndex === position;

            if (isActive) {
              rioClient.sendEvent(_objectSpread(_objectSpread({}, viewBaseEvent), {}, {
                data: {
                  component_view: {
                    state: 'expanded',
                    component: {
                      name: 'nav menu',
                      type: 'drop down',
                      text: menuItems.map(function (m) {
                        return m.text.trim();
                      }).join('|').toLowerCase(),
                      region_name: "side nav|".concat(text.toLowerCase().trim())
                    }
                  }
                }
              }));
            }

            return {
              header: /*#__PURE__*/React.createElement(LinkAccordionHeader, null, /*#__PURE__*/React.createElement(NavMobileTopLevelMenuItem, {
                "data-test": "header-mobile-hamburger-menu-item-".concat(position),
                onClick: function onClick(e) {
                  var value = text.toLowerCase();
                  rioClient.sendEvent(_objectSpread(_objectSpread({}, clickBaseEvent), {}, {
                    data: {
                      interaction: {
                        type: 'select',
                        element: {
                          text: text,
                          value: value,
                          type: 'link',
                          name: 'nav shortcut',
                          region_name: 'side nav'
                        }
                      }
                    }
                  }));
                  _onClick === null || _onClick === void 0 ? void 0 : _onClick(e);
                } // we need to add one due to the Home link
                ,
                ref: topLevelLinksRef.current[position],
                key: text
              }, ItemIcon && /*#__PURE__*/React.createElement(ItemIcon, {
                color: isActive ? colors.white : colors.cheggOrange,
                size: "small",
                role: "presentation",
                "data-test": "header-mobile-hamburger-menu-item-icon-".concat(position)
              }), activeSubLink === text && /*#__PURE__*/React.createElement(VisuallyHidden, null, "Current Page:", ' '), /*#__PURE__*/React.createElement(NavLinkText, {
                extraMargin: !ItemIcon,
                "data-test": "header-mobile-hamburger-menu-item-text-".concat(position)
              }, text)), /*#__PURE__*/React.createElement(MobileAccordionDivider, {
                inverted: isActive
              })),
              panel: /*#__PURE__*/React.createElement(LinkAccordionPanel, null, menuItems.map(function (_ref3, index) {
                var courseUuid = _ref3.courseUuid,
                    childHref = _ref3.href,
                    childText = _ref3.text,
                    onChildClick = _ref3.onClick;
                return /*#__PURE__*/React.createElement(SecondLevelNavLink, {
                  key: childText,
                  href: "".concat(envUrl).concat(childHref),
                  isActive: false,
                  onClick: function onClick(e) {
                    rioClient.sendEvent(_objectSpread(_objectSpread({}, clickBaseEvent), {}, {
                      data: {
                        interaction: {
                          type: 'select',
                          element: {
                            type: 'link',
                            text: childText,
                            name: 'nav menu item',
                            value: courseUuid || childText.toLowerCase().trim(),
                            region_name: "side nav|".concat(text.toLowerCase().trim())
                          }
                        }
                      }
                    }));
                    onChildClick === null || onChildClick === void 0 ? void 0 : onChildClick(e);
                  },
                  "data-test": "header-mobile-hamburger-menu-item-".concat(position, "-subitem-").concat(index)
                }, /*#__PURE__*/React.createElement(NavLinkText, null, childText));
              }))
            };
          })
        });
      } else {
        var _ref4 = item,
            text = _ref4.text,
            position = _ref4.position,
            href = _ref4.href,
            icon = _ref4.icon,
            onClick = _ref4.onClick;
        var ItemIcon = icon;
        var isActive = activeAccordionIndex === position;
        return /*#__PURE__*/React.createElement(NavMobileTopLevelLink, {
          href: "".concat(envUrl).concat(href),
          "data-test": "header-mobile-hamburger-menu-item-".concat(position),
          isActive: isActive,
          ref: topLevelLinksRef.current[position],
          onClick: onClick,
          key: text
        }, ItemIcon && /*#__PURE__*/React.createElement(ItemIcon, {
          color: isActive ? colors.white : colors.cheggOrange,
          size: "small",
          role: "presentation",
          "data-test": "header-mobile-hamburger-menu-item-icon-".concat(position)
        }), activeSubLink === text && /*#__PURE__*/React.createElement(VisuallyHidden, null, "Current Page: "), /*#__PURE__*/React.createElement(NavLinkText, {
          extraMargin: !ItemIcon,
          "data-test": "header-mobile-hamburger-menu-item-text-".concat(position)
        }, text));
      }
    });
  }(), userDataLoading ? /*#__PURE__*/React.createElement(UserInfoLoader, {
    "data-test": "header-mobile-userinfo-loader"
  }) : /*#__PURE__*/React.createElement(UserInfoUL, {
    ref: userUlRef
  }, /*#__PURE__*/React.createElement(UserInfoLI, null, user && /*#__PURE__*/React.createElement(UserProfileLink, {
    href: "".concat(envUrl, "my/profile"),
    "data-test": "header-mobile-user-menu-info"
  }, /*#__PURE__*/React.createElement(SmallPaddedAvatar, {
    size: 'medium',
    name: user.fname,
    image: {
      src: user.avatar,
      alt: ''
    },
    "data-test": "header-mobile-user-avatar"
  }), /*#__PURE__*/React.createElement(UserTextContainer, null, /*#__PURE__*/React.createElement(UserProfileText, {
    "data-test": "header-mobile-user-menu-username"
  }, user.fname, " ", user.lname), /*#__PURE__*/React.createElement(UserEmailText, {
    "data-test": "header-desktop-user-menu-email"
  }, user.email)))), user && userLinks.map(function (_ref5, index) {
    var text = _ref5.text,
        href = _ref5.href,
        onClick = _ref5.onClick;
    return /*#__PURE__*/React.createElement(UserInfoLI, {
      key: text
    }, /*#__PURE__*/React.createElement(UserInfoLink, {
      href: "".concat(envUrl).concat(href),
      "data-test": "header-desktop-user-menu-link-".concat(index),
      onClick: onClick
    }, /*#__PURE__*/React.createElement("span", null, text)));
  })), /*#__PURE__*/React.createElement(MenuFooter, null, /*#__PURE__*/React.createElement(Copyright, null, "\xA9 2003-", new Date().getFullYear(), " Chegg Inc. All rights reserved."), /*#__PURE__*/React.createElement(MenuFooterTerms, null, /*#__PURE__*/React.createElement("a", {
    href: "".concat(envUrl, "termsofuse"),
    "data-test": "header-tou"
  }, "Privacy & Terms"))))));
});
MobileHamburgerMenu.displayName = 'MobileHamburgerMenu';
export default MobileHamburgerMenu;